import React, { useCallback, useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "../../misc/Tabs";
import { CombinedMedia, TabsProps } from "./types";
import { getAuthHeader } from "../../../utils/authHeader";
import axios from "axios";
import { toast } from "react-toastify";
import CustomToast from "../../misc/CustomToast";
import ImagesTab from "./tabs/ImagesTab";
import VideosTab from "./tabs/VideosTab";
import DetailsTab from "./tabs/DetailsTab";
import TabButtons from "./tabs/TabButtons";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { getChanges } from "./getChanges";
import { useUploadNavigationGuard } from "../../../hooks/useUploadNavigationGuard";
import UploadNavigationWarning from "../../misc/UploadNavigationWarning";

const TabsComponent: React.FC<TabsProps> = ({
    images,
    videos,
    setIsLoading,
    carouselApi,
    propertyData,
    fetchListing,
    fetchAssets,
    setPropertyData,
    originalPropertyData,
    setOriginalPropertyData,
    combinedMedia,
    mlsApproved,
    setShowSaveWarning,
    activeTab,
    setActiveTab,
    combinedImages,
    combinedVideos,
    carouselIndices,
    setCarouselIndices,
    isUploading,
    setIsUploading,
    syncDetails,
    setSyncDetails,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState<CombinedMedia[]>([]);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    // Upload navigation guard
    const [activeUploads, setActiveUploads] = useState(0);
    const [showNavigationWarning, setShowNavigationWarning] = useState(false);
    const [showTabWarning, setShowTabWarning] = useState(false);
    const [pendingTab, setPendingTab] = useState<"images" | "videos" | "details" | null>(null);
    
    const { confirmNavigation } = useUploadNavigationGuard({
        uploadCount: activeUploads,
        onNavigationAttempt: () => {
            setShowNavigationWarning(true);
        },
    });

    useEffect(() => {
        if (!isEditing) {
            setSelectedMedia([]);
        }
    }, [isEditing]);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab]);
    
    // Handle upload count changes from child tabs
    const handleActiveUploadsChange = useCallback((count: number) => {
        setActiveUploads(count);
    }, []);
    
    // Auto-close navigation warning when uploads complete
    useEffect(() => {
        if (activeUploads === 0 && showNavigationWarning) {
            setShowNavigationWarning(false);
        }
    }, [activeUploads, showNavigationWarning]);
    
    useEffect(() => {
        if (activeUploads === 0 && showTabWarning) {
            setShowTabWarning(false);
        }
    }, [activeUploads, showTabWarning]);

    const toggleSelectMedia = (media: CombinedMedia) => {
        setSelectedMedia(
            selectedMedia.some((m) => m.id === media.id)
                ? selectedMedia.filter((m) => m.id !== media.id)
                : [...selectedMedia, media]
        );
    };

    const handleUploadComplete = useCallback(() => {
        fetchListing();
        fetchAssets();
    }, [fetchListing, fetchAssets]);
    
    // Handle tab changes with upload protection
    const handleTabChange = (newTab: "images" | "videos" | "details") => {
        
        // Only warn if switching away from a tab with active uploads
        if (activeUploads > 0 && activeTab !== newTab && (activeTab === 'images' || activeTab === 'videos')) {
            setPendingTab(newTab);
            setShowTabWarning(true);
        } else {
            setActiveTab(newTab);
        }
    };
    
    const confirmTabChange = () => {
        sessionStorage.removeItem('uploadsInterrupted');
        // Reset upload count before switching
        setActiveUploads(0);
        
        if (pendingTab) {
            setActiveTab(pendingTab);
            setPendingTab(null);
        }
        setShowTabWarning(false);
    };
    
    const cancelTabChange = () => {
        setPendingTab(null);
        setShowTabWarning(false);
    };
    
    const handleConfirmNavigation = () => {
        sessionStorage.removeItem('uploadsInterrupted');
        setActiveUploads(0);
        setShowNavigationWarning(false);
        confirmNavigation();
    };
    
    const handleCancelNavigation = () => {
        setShowNavigationWarning(false);
    };

    const handleSave = async () => {
        if (isSaving) return;
        if (!propertyData || !originalPropertyData) return;

        // Validate required fields for non-MLS listings when editing
        const isMlsListing = propertyData.listingType === "MLSListing";
        if (!isMlsListing && isEditing) {
            const newErrors: { [key: string]: string } = {};

            // Validate price
            if (!propertyData?.currentPrice || propertyData.currentPrice <= 0) {
                newErrors.currentPrice = "Price is required and must be greater than 0.";
            }

            // Validate description
            if (!propertyData?.legalDescription?.trim()) {
                newErrors.legalDescription = "Description is required.";
            }

            setErrors(newErrors);

            if (Object.keys(newErrors).length > 0) {
                toast.error(
                    <CustomToast
                        message="Please fill the required fields."
                        type="error"
                    />,
                    { autoClose: 3000 }
                );

                // Scroll to first error field
                const firstErrorField = document.querySelector(
                    `[name="${Object.keys(newErrors)[0]}"]`
                );
                if (firstErrorField) {
                    firstErrorField.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                    (firstErrorField as HTMLElement).focus();
                }

                return;
            }
        }

        const changes = getChanges(originalPropertyData, propertyData);

        if (Object.keys(changes).length === 0) {
            setIsEditing(false);
            toast.success(<CustomToast message="Saved." type="success" />, {
                autoClose: 3000,
            });
            return;
        }

        try {
            setIsSaving(true);
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const response = await axios.patch(
                `${backendUrl}/listing`,
                {
                    ...changes,
                    listingId: propertyData._id,
                    auto:
                        (propertyData.listingType === "MLSListing" &&
                            mlsApproved) ||
                        (propertyData.listingType === "RentcastListing" &&
                            !mlsApproved),
                },
                { headers }
            );

            if (response.status === 200) {
                setOriginalPropertyData(propertyData);
                setIsEditing(false);
                fetchListing();
                setShowSaveWarning(true);
            } else {
                throw new Error("Failed to update listing.");
            }
        } catch (error) {
            console.error("Error saving listing:", error);
            toast.error(
                <CustomToast message="Failed to save changes." type="error" />,
                { autoClose: 3000 }
            );
        } finally {
            setIsSaving(false);
        }
    };

    const isMobile = useIsMobile(1200);

    const handleCarouselScroll = (newIndex: number) => {
        setCarouselIndices((prev) => ({
            ...prev,
            [activeTab]: newIndex,
        }));
    };

    return (
        <div className={`${isMobile ? "my-2" : "my-0 px-0"}`}>
            <Tabs
                value={activeTab}
                onValueChange={(value: string) =>
                    handleTabChange(value as "images" | "videos" | "details")
                }
            >
                <div
                    className={`flex ${
                        isMobile
                            ? "flex-col"
                            : "flex-row justify-between items-center"
                    }`}
                >
                    <TabsList
                        className={`${
                            isMobile
                                ? "flex overflow-x-auto space-x-2 pb-2"
                                : "space-x-4 w-full"
                        }`}
                    >
                        <TabsTrigger value="details">
                            Details
                        </TabsTrigger>
                        <TabsTrigger value="images">
                            Images {isMobile ? "" : `(${images.length})`}
                        </TabsTrigger>
                        <TabsTrigger value="videos">
                            Videos {isMobile ? "" : `(${videos.length})`}
                        </TabsTrigger>
                    </TabsList>

                    <TabButtons
                        isEditing={isEditing}
                        setIsEditing={setIsEditing}
                        selectedMedia={selectedMedia}
                        handleSave={handleSave}
                        isSaving={isSaving}
                        setIsLoading={setIsLoading}
                        propertyData={propertyData}
                        fetchListing={fetchListing}
                        fetchAssets={fetchAssets}
                        setSelectedMedia={setSelectedMedia}
                        mlsApproved={mlsApproved}
                        isMobile={isMobile}
                        originalPropertyData={originalPropertyData}
                        setPropertyData={setPropertyData}
                        mediaLength={selectedMedia.length}
                        syncDetails={syncDetails}
                        setSyncDetails={setSyncDetails}
                    />
                </div>

                {/* Details Tab */}
                <DetailsTab
                    propertyData={propertyData}
                    setPropertyData={setPropertyData}
                    isEditing={isEditing}
                    handleSave={handleSave}
                    mlsApproved={mlsApproved}
                    isSaving={isSaving}
                    errors={errors}
                    setErrors={setErrors}
                />

                {/* Images Tab */}
                <ImagesTab
                    images={images}
                    listingId={propertyData._id}
                    handleUploadComplete={handleUploadComplete}
                    toggleSelectMedia={toggleSelectMedia}
                    selectedMedia={selectedMedia}
                    isEditing={isEditing}
                    carouselApi={carouselApi}
                    combinedMedia={combinedImages}
                    mlsApproved={mlsApproved}
                    setSelectedMedia={setSelectedMedia}
                    setIsLoading={setIsLoading}
                    itemsPerPage={10}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    onCarouselScroll={handleCarouselScroll}
                    propertyStatus={propertyData.status || ""}
                    isUploading={isUploading}
                    setIsUploading={setIsUploading}
                    listingType={propertyData.listingType}
                    isSyncing={propertyData.isSyncing || false}
                    onActiveUploadsChange={handleActiveUploadsChange}
                    activeTab={activeTab}
                />

                {/* Videos Tab */}
                <VideosTab
                    videos={videos}
                    listingId={propertyData._id}
                    address1={propertyData.formattedAddress.split(",")[0]}
                    handleUploadComplete={handleUploadComplete}
                    toggleSelectMedia={toggleSelectMedia}
                    selectedMedia={selectedMedia}
                    isEditing={isEditing}
                    carouselApi={carouselApi}
                    combinedMedia={combinedVideos}
                    mlsApproved={mlsApproved}
                    setSelectedMedia={setSelectedMedia}
                    setIsLoading={setIsLoading}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    itemsPerPage={10}
                    onCarouselScroll={handleCarouselScroll}
                    propertyStatus={propertyData.status || ""}
                    isUploading={isUploading}
                    setIsUploading={setIsUploading}
                    listingType={propertyData.listingType}
                    isSyncing={propertyData.isSyncing || false}
                    onActiveUploadsChange={handleActiveUploadsChange}
                    activeTab={activeTab}
                />
            </Tabs>
            
            {/* Navigation Warning Modal */}
            <UploadNavigationWarning
                isOpen={showNavigationWarning}
                onClose={handleCancelNavigation}
                onLeave={handleConfirmNavigation}
                onStay={handleCancelNavigation}
            />
            
            {/* Tab Switch Warning Modal */}
            <UploadNavigationWarning
                isOpen={showTabWarning}
                onClose={cancelTabChange}
                onLeave={confirmTabChange}
                onStay={cancelTabChange}
            />
        </div>
    );
};

export default TabsComponent;
